/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { IShift } from "../../interfaces/ShiftInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { getShifts, postShift, patchShift, deleteShift } from "../../api/shift";

export const shiftsQueryKey = "shifts";

// use shifts
export const useShifts = (): UseQueryResult<Array<IShift>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [shiftsQueryKey, resourceCentre?.id],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getShifts({
        resourceCentreId: Number(resourceCentre.id)
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

// use post shift
export const usePostShift = (): UseMutationResult<
  ApiResponse<IShift>,
  Error,
  Partial<IShift>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (data: Partial<IShift>) => postShift(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [shiftsQueryKey, resourceCentre?.id] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Shift created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create shift",
          variant: "error"
        })
      );
    }
  });
};

// use patch shift
export const usePatchShift = (): UseMutationResult<
  ApiResponse<IShift>,
  Error,
  { id: number | string; data: Partial<IShift> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: ({ id, data }) => patchShift({ id, data }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [shiftsQueryKey, resourceCentre?.id] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Shift updated successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update shift",
          variant: "error"
        })
      );
    }
  });
};

// use delete shift
export const useDeleteShift = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (id) => deleteShift(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [shiftsQueryKey, resourceCentre?.id] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Shift deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete shift",
          variant: "error"
        })
      );
    }
  });
};
